import React from 'react';
import map from 'lodash/map';
import toString from 'lodash/toString';
import some from 'lodash/some';

import {
  ID,
  FetchItemsSort,
  FetchItemsSortableField,
  FetchItemsSortItems,
  PositionStyleEnum,
  GADataType
} from '../../../../../../../types';
import { ProjectNanoID } from '../../../../../../projects/projectsTypes';
import { CompactIndexTableHeaderSortFilterCellFilterField } from '../../CompactIndexTableHeaderSortFilterCell.types';

import { PopoverWithPortal } from '../../../../../../../helpers/Popover';

import { AscDescButtonsSort } from '../../../../../../../helpers/sort/AscDescButtonsSort';
import { PureButtonHelper } from '../../../../../../../helpers/buttons/PureButtonHelper';
import { UniversalFilter } from '../../../../../../../helpers/filters/UniversalFilter';

import { PopoverPlacement } from '../../../../../../../helpers/Popover/popoverConstants';
import { words } from '../../../../../../../locales/keys';

const distanceOffset = 12;

interface CompactIndexTableHeaderSortFilterCellPopoverProps<FiltersType> {
  withSort?: boolean;
  viewId: ID;
  filterFields: CompactIndexTableHeaderSortFilterCellFilterField<FiltersType>[];
  currentSort?: FetchItemsSort;
  ascSort?: FetchItemsSortableField;
  descSort?: FetchItemsSortableField;
  localFiltersState: Partial<FiltersType>;
  referencePopoverElement?: HTMLElement | null;
  setLocalFiltersState: (cb: (prevFilters: FiltersType) => FiltersType) => void;
  closePopover?: () => void;
  onSort?: FetchItemsSortItems;
  onApply?: () => void;
  onClear?: () => void;
  projectNanoId?: ProjectNanoID;
}

function CompactIndexTableHeaderSortFilterCellPopover<
  FiltersType extends Record<string, any>
>({
  dataGa,
  viewId,
  filterFields,
  currentSort,
  withSort,
  ascSort,
  descSort,
  localFiltersState,
  setLocalFiltersState,
  referencePopoverElement,
  closePopover,
  onSort,
  onApply,
  onClear,
  projectNanoId
}: CompactIndexTableHeaderSortFilterCellPopoverProps<FiltersType> &
  GADataType) {
  const isAnyFieldWithInitialAutoFocus = some(
    filterFields,
    ({ initialAutoFocus }) => initialAutoFocus
  );

  return (
    <PopoverWithPortal
      className="bg-gray-100 dark:bg-gray-850 border dark:border-gray-800 rounded-md shadow min-w-60 max-w-xs md:max-w-md 2xl:max-w-lg font-normal z-20"
      closePopover={closePopover}
      distanceOffset={distanceOffset}
      positionStyle={PositionStyleEnum.fixed}
      placement={PopoverPlacement.BOTTOM_START}
      referenceElement={referencePopoverElement}
      closeOnOuterClick
    >
      <div className="px-4 py-2">
        <div className="space-y-2">
          {withSort ? (
            <AscDescButtonsSort
              ascValue={ascSort}
              dataGa={dataGa}
              descValue={descSort}
              sort={currentSort}
              onSort={onSort}
            />
          ) : null}

          {map(
            filterFields,
            (
              { fieldName, type, i18nName, options, initialAutoFocus },
              index
            ) => {
              const isFirst = index === 0;
              const withInitialAutofocus =
                initialAutoFocus ||
                (isFirst && !isAnyFieldWithInitialAutoFocus);
              return (
                <UniversalFilter<Partial<FiltersType>>
                  key={toString(fieldName)}
                  viewId={viewId}
                  filterValue={localFiltersState?.[fieldName]}
                  fieldName={fieldName}
                  type={type}
                  options={options}
                  i18nName={i18nName}
                  initialAutoFocus={withInitialAutofocus}
                  setFilters={setLocalFiltersState}
                  projectNanoId={projectNanoId}
                />
              );
            }
          )}
        </div>
      </div>
      <div className="px-4 py-2 border-t dark:border-gray-800">
        <div className="flex justify-between">
          <div>
            <PureButtonHelper
              className="py-1 pl-2 pr-2 rounded-md inline-flex items-center whitespace-nowrap text-sm font-medium leading-5 focus:ring-base border border-gray-300 dark:border-gray-700 shadow-sm bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              dataGa={`${dataGa}-clear-button`}
              i18nText={words.clear}
              onClick={onClear}
            />
          </div>
          <div>
            <PureButtonHelper
              className="py-1 pl-2 pr-2 rounded-md inline-flex items-center whitespace-nowrap text-sm font-medium leading-5 focus:ring-base border border-gray-300 dark:border-gray-700 shadow-sm bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              dataGa={`${dataGa}-close-button`}
              i18nText={words.close}
              onClick={closePopover}
            />
            <PureButtonHelper
              className="ml-1 py-1 pl-2 pr-2 rounded-md inline-flex items-center whitespace-nowrap text-sm font-medium leading-5 focus:ring-base text-white bg-blue-500 hover:bg-blue-600 shadow-sm hover:shadow-md"
              dataGa={`${dataGa}-apply-button`}
              i18nText={words.apply}
              onClick={onApply}
            />
          </div>
        </div>
      </div>
    </PopoverWithPortal>
  );
}

export default CompactIndexTableHeaderSortFilterCellPopover;
